import React from 'react'
import { useTranslation } from 'react-i18next'
import { useField, useFormikContext } from 'formik'
import { Grid, Paper } from '@mui/material'

import { IRevenueGroup, IRevenueGroupAdministration } from '@cbgms/api'
import { RevenueGroupKind, VatType, VehicleSalesType, WorkorderLineType } from '@cbgms/api/modules/enums'
import { enumToKeys, enumToOptions, formatEnum } from '@cbgms/base/utils/helpers'
import { Checkbox, Dropdown, DropdownOption, TextField } from '@carsys/form'

import { IAutoCorrectMode } from 'core/widgets/MaterialVatDropdown/useGetVatTypesOptions'
import VatAutocomplete from 'core/widgets/VatDropdown/VatSelection'
import { PERMISSION_VEHICLESALES_READ } from 'utils/Subscription/Permissions'
import { useHasPermission } from 'utils/Subscription/RequiredPermissions'
import { generateAdministrationWithMissingLedgers } from './utils'

interface IProps {
  /**
   * This property disables the fields `Kind`, `ProductType` and `VehicleSalesType`.
   *
   * The use case for this is when creating a revenue group
   * from a specific context. For example when on the sundry view
   * the `Kind` should be `workshop` and `ProductType` should be `sundries`.
   * The user at this point is not allowed to change these values.
   */
  hasReadOnlyInitialValues?: boolean
  departments: any[]
}

export interface RevenueGroupFormValues extends Partial<IRevenueGroup> {
  Name: string
  VatLimiter: VatType[]
}

export interface RevenueGroupAdministrationFormValues extends Partial<IRevenueGroupAdministration> {
  Name: string
  VatLimiter: VatType[]
}

export const MainRevenueGroupForm: React.FC<IProps> = props => {
  const { hasReadOnlyInitialValues, departments } = props
  const { t } = useTranslation()
  const [hasVehicleSalesPermission] = useHasPermission(PERMISSION_VEHICLESALES_READ)
  const {
    values: { VatLimiter, Administrations, UUID, VatWhenUsedAsDefault, ProductType },
    setFieldValue
  } = useFormikContext<IRevenueGroupAdministration>()

  let revenueGroupKindTypeOptions = enumToOptions(RevenueGroupKind, 'EnumRevenueGroupKind')
  if (!hasVehicleSalesPermission) {
    revenueGroupKindTypeOptions = revenueGroupKindTypeOptions.filter(option => option.key !== RevenueGroupKind.CarSales)
  }
  const productTypeOptions = enumToOptions(WorkorderLineType, 'EnumWorkorderLineType').filter(a => a.key !== WorkorderLineType.None)
  const vehicleSalesTypeOptions = enumToOptions(VehicleSalesType, 'EnumRevenueVehicleSalesType').filter(
    a => a.key !== VehicleSalesType.None
  )
  const [kind] = useField('Kind')
  const [revenueKind, setRevenueKind] = React.useState(kind.value)

  const prevRevenueKindRef = React.useRef()

  const prevRevenueKind = prevRevenueKindRef.current

  React.useEffect(() => {
    prevRevenueKindRef.current = revenueKind
  })

  React.useEffect(() => {
    setRevenueKind(kind.value)
    if (kind.value === RevenueGroupKind.CarSales && (UUID === '' || prevRevenueKind === RevenueGroupKind.Workshop)) {
      const allVatTypes = enumToKeys(VatType)
        .filter(key => VatType[key] === 'high' || VatType[key] === 'margin')
        .map(key => VatType[key])
      setFieldValue('VatLimiter', allVatTypes)
      onVatLimiterChange(allVatTypes)
    }
  }, [kind.value, UUID])

  const vatWhenUsedAsDefaultOptions = (VatLimiter || []).map(vatEnumValue => ({
    key: vatEnumValue,
    text: formatEnum('EnumVatType', vatEnumValue),
    value: vatEnumValue
  }))

  const onVatLimiterChange = React.useCallback(
    (vatLimiter: VatType[]) => {
      // Logic for clearing the default VAT Type if it doesn't exist in VatLimiter anymore
      if (!vatLimiter.some(vatType => vatType === VatWhenUsedAsDefault)) {
        setFieldValue('VatWhenUsedAsDefault', '')
      }
      // Logic for Administrations Ledgers
      if (Administrations) {
        Administrations?.forEach((_administration, index) => {
          const ledgers = generateAdministrationWithMissingLedgers(Administrations[index], vatLimiter, departments, UUID).Ledgers

          setFieldValue(`Administrations[${index}].Ledgers`, ledgers)
        })
      }
    },
    [Administrations, UUID, VatWhenUsedAsDefault, ProductType]
  )

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item md={4} xs={6}>
          <TextField label={t('revenue_group.name')} name='Name' required />
        </Grid>
        <Grid item md={4} xs={6}>
          <Dropdown label={t('revenue_group.kind')} name='Kind' required disabled={hasReadOnlyInitialValues}>
            {revenueGroupKindTypeOptions.map(opt => (
              <DropdownOption key={opt.key} value={opt.value}>
                {opt.text}
              </DropdownOption>
            ))}
          </Dropdown>
        </Grid>
        <Grid item md={4} xs={12}>
          <Checkbox label={t('revenue_group.hide_in_general_selection')} name='HideInGeneralSelection' />
        </Grid>
        {kind.value === RevenueGroupKind.Workshop && (
          <>
            <Grid item xs={4}>
              <Dropdown label={t('revenue_group.producttype')} name='ProductType' required disabled={hasReadOnlyInitialValues}>
                {productTypeOptions.map(opt => (
                  <DropdownOption key={opt.key} value={opt.value}>
                    {opt.text}
                  </DropdownOption>
                ))}
              </Dropdown>
            </Grid>
            <Grid item xs={4}>
              <Checkbox label={t('revenue_group.is_default_for_product_type')} name='IsDefaultForProductType' />
            </Grid>
          </>
        )}
        {kind.value === RevenueGroupKind.CarSales && (
          <Grid item xs={4}>
            <Dropdown label={t('revenue_group.vehiclesalestype')} name='VehicleSalesType' required disabled={hasReadOnlyInitialValues}>
              {vehicleSalesTypeOptions.map(opt => (
                <DropdownOption key={opt.key} value={opt.value}>
                  {opt.text}
                </DropdownOption>
              ))}
            </Dropdown>
          </Grid>
        )}
        <Grid item xs={4}>
          <VatAutocomplete
            required
            fullWidth
            autoCorrectMode={IAutoCorrectMode.preferDefault}
            label={t('revenue_group.vat_type')}
            placeholder={t('revenue_group.vat_type')}
            multiple
            name='VatLimiter'
            onChange={(value: VatType[]) => {
              onVatLimiterChange(value)
            }}
          />
        </Grid>
        <Grid item xs={4}>
          <Dropdown required label={t('revenue_group.vat_when_used_as_default')} name='VatWhenUsedAsDefault'>
            {vatWhenUsedAsDefaultOptions.map(opt => (
              <DropdownOption key={opt.key} value={opt.value}>
                {opt.text}
              </DropdownOption>
            ))}
          </Dropdown>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default MainRevenueGroupForm
